import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useGenerationSettings } from "~/entities/model";
import { startGeneration, type Chat, type ChatAttachment } from "~/entities/chat";
import { chatDetailKey, chatJobsKey, chatMessagesKey } from "~/entities/chat/useActiveChat";
import { useActiveWorkspace } from "~/entities/workspace";
import { getModelById } from "~/shared/data/chat-models";
import { useChatErrorsStore } from "~/shared/store/useChatErrorsStore";
import { useChatSettingsStore } from "~/shared/store/useChatSettingsStore";
import { useDraftChatStore } from "~/shared/store/useDraftChatStore";

import { buildAttachmentPayload } from "./attachment-payload";
import { toChatGenerationError } from "./generation-error";
import { resolveOrderSettings, type RepeatOrder } from "./order-settings";
import { buildGenerationSettings } from "./settings-payload";

export type { RepeatOrder };

interface StartGenerationInput {
    chatUuid: string;
    messageId: string;
    inputAttachments: ChatAttachment[];
    /**
     * Задан — запрос собирается по нему, а не по текущему бару. Стор бара при
     * этом НЕ переписывается: человек мог набирать следующий запрос, и повтор
     * упавшей генерации не должен трогать его черновик.
     */
    repeat?: RepeatOrder;
}

export function useStartGeneration() {
    const qc = useQueryClient();
    const { activeWorkspaceId } = useActiveWorkspace();
    const settings = useChatSettingsStore();
    const settingsQuery = useGenerationSettings();
    const setError = useChatErrorsStore((s) => s.setError);
    const clearError = useChatErrorsStore((s) => s.clearError);
    const getPending = useDraftChatStore((s) => s.getPending);
    const getElementIds = useDraftChatStore((s) => s.getElementIds);

    return useCallback(
        async ({ chatUuid, messageId, inputAttachments, repeat }: StartGenerationInput) => {
            if (!activeWorkspaceId) {
                setError(messageId, { message: "Пространство не выбрано" });
                return;
            }

            // Повтор уходит настройками снимка, обычная отправка — настройками бара.
            const effective = resolveOrderSettings(settings, repeat);

            if (!effective.modelId) {
                setError(messageId, { message: "Модель не выбрана" });
                return;
            }

            // project/scene берём из самого чата (источник истины — таблица
            // чата): привязан к сцене → шлём оба; только к проекту → project;
            // workspace-only чат → ничего. Backend принимает их опциональными,
            // а заголовки не уходят, когда значение undefined.
            const chat = qc.getQueryData<Chat>(chatDetailKey(chatUuid));

            const currentMode = getModelById(settingsQuery.data, effective.modelId)?.modes.find(
                (m) => m.name === effective.mode,
            );
            // Роль кадра и канал референса живут на pending-вложении, а при повторе
            // очередь бара пуста (страницу перезагрузили) или занята следующим
            // запросом — раскладку берём из снимка, иначе референс уехал бы обычным
            // кадром, а пара кадров рассыпалась.
            const pending = repeat ? repeat.attachments : getPending(chatUuid);
            const channels = buildAttachmentPayload(inputAttachments, currentMode?.imageRoles ?? [], pending);

            try {
                clearError(messageId);
                await startGeneration(
                    {
                        chatUuid,
                        messageId,
                        modelName: effective.modelId,
                        generationMode: effective.mode,
                        settings: buildGenerationSettings(effective, currentMode),
                        ...channels,
                        // Элементы библиотеки — только для режимов, которые их
                        // принимают: выбор мог остаться от предыдущего режима.
                        referenceElementIds:
                            (currentMode?.maxElements ?? 0) > 0
                                ? (repeat?.elementIds ?? getElementIds(chatUuid))
                                : [],
                    },
                    {
                        workspaceId: activeWorkspaceId,
                        projectId: chat?.projectId ?? undefined,
                        sceneId: chat?.sceneId ?? undefined,
                    },
                );
                void qc.invalidateQueries({ queryKey: chatJobsKey(chatUuid) });
                void qc.invalidateQueries({ queryKey: chatMessagesKey(chatUuid) });
            } catch (err) {
                console.error("startGeneration failed", err);
                setError(messageId, toChatGenerationError(err));
            }
        },
        [
            activeWorkspaceId,
            settings,
            settingsQuery.data,
            qc,
            setError,
            clearError,
            getPending,
            getElementIds,
        ],
    );
}
